import { constants as config } from '@config';
import memoizerific from 'memoizerific';
import * as TextStylingService from 'src/services/text-styling';

export default () => ({
  // Cache for recently calculated text metrics
  textMetricsCache: {
    // Hidden element to measure text width
    hiddenElement: null
  },

  splitter: window.INKIVE.EditorFacade && new window.INKIVE.EditorFacade.GraphemeSplitter(),
  isFirefox: navigator.userAgent.indexOf('Firefox') > 0,
  isChrome: navigator.userAgent.indexOf('Chrome') > 0,

  memoizedCalculateTextWidth: memoizerific(5000)(function (content, fontFamily, fontWeight, fontStretch, fontStyle) {
    // Memoize 5000 results since large document has up to 3000 words
    if (!content) return 0;

    const context = INKIVE.Text.Helpers.textMetricsCache.hiddenElement.getContext('2d');
    context.font = `${fontStretch} ${fontStyle} ${fontWeight} ${config.text.defaultStyle.fontSize}px "${fontFamily}"`;

    const metrics = context.measureText(content);
    return metrics.width;
  }),

  getLineBBox (textarea, startIndex, content, keepSpace = false) {
    if (!content || content.length === 0) return null;
    const text = content;
    if (text.length === 0) return null;

    const defaultStyle = config.text.defaultStyle;

    // Try to fetch from document
    if (!this.textMetricsCache) {
      this.textMetricsCache = {};
    }

    // Try to fetch from document
    if (!this.textMetricsCache.hiddenElement) {
      this.textMetricsCache.hiddenElement = document.querySelector('canvas#text-metrics');
    }

    // Not found, create it
    if (!this.textMetricsCache.hiddenElement) {
      this.textMetricsCache.hiddenElement = document.createElement('canvas');
      this.textMetricsCache.hiddenElement.id = 'text-metrics';
      this.textMetricsCache.hiddenElement.hidden = true;
      document.body.append(this.textMetricsCache.hiddenElement);
    }

    const parentFontSize = textarea.font_size;
    const parentFontFamily = this.sanitizeFontFamily(textarea.font_family);
    const parentFontWeight = this.calculateNumericalFontWeight(textarea.font_weight);
    const parentFontStretch = textarea.font_stretch;
    const parentFontStyle = textarea.font_style;

    const activeStyles = TextStylingService.getActiveStyles(startIndex, content, textarea.styles);
    const insertedStyles = TextStylingService.insertParentStyles(startIndex, content, activeStyles, parentFontSize, parentFontFamily, parentFontWeight, parentFontStretch, parentFontStyle);

    let result = 0;
    for (let i = 0; i < insertedStyles.length; i++) {
      const span = insertedStyles[i];
      const spanContent = span.content;
      const spanFontFamily = this.sanitizeFontFamily(span['font-family']);
      const spanFontWeight = this.calculateNumericalFontWeight(span['font-weight']);
      const spanFontStretch = span['font-stretch'] || 'normal';
      const spanFontStyle = span['font-style'] || 'normal';

      // Memoized function always calculate text width with default font size 10px
      const fontSizeFactor = Number(span['font-size']) / defaultStyle.fontSize;

      const contentWidth = this.memoizedCalculateTextWidth(spanContent, spanFontFamily, spanFontWeight, spanFontStretch, spanFontStyle) * fontSizeFactor;
      result += contentWidth;
    }

    return { width: result };
  },

  sanitizeFontFamily (fontFamily) {
    return fontFamily.replace(/['"]+/g, '');
  },

  calculateNumericalFontWeight (fontWeight) {
    return parseInt(fontWeight) || config.text.defaultFontWeight[fontWeight] || config.text.defaultFontWeight.normal;
  },

  // Compare two text content and extract the changed line indices
  getChangedLines (oldContent, newContent, cursorPosition, newLetters) {
    const oldCursorPosition = cursorPosition - newLetters;
    const oldLines = oldContent.substring(0, oldCursorPosition).split(/\r\n|\r|\n/g);
    const newLines = newContent.substring(0, cursorPosition).split(/\r\n|\r|\n/g);

    if (newLines.length > oldLines.length) {
      const oldLineIndices = Object.keys(oldLines);
      const lines = [];
      newLines.forEach((_, i) => {
        oldLineIndices.indexOf(String(i)) === -1 && lines.push(i);
      });
      if (lines.length > 0) return { mode: 'added', lines };
    }

    if (newLines.length < oldLines.length) {
      const newLinesIndices = Object.keys(newLines);
      const lines = [];
      oldLines.forEach((_, i) => {
        newLinesIndices.indexOf(String(i)) === -1 && lines.push(i);
      });
      if (lines.length > 0) return { mode: 'removed', lines };
    }

    return { mode: 'none' };
  },

  // Replace large dx values with space
  convertDxToSpace (textarea) {
    const texts = textarea.querySelectorAll('text');

    texts.forEach(textElement => {
      const dxString = textElement.getAttribute('dx');
      if (!dxString || dxString.indexOf(' ') === -1) return;
      const dx = dxString.split(' ');
      if (dx.length !== textElement.textContent.length) return;

      const tspans = textElement.querySelectorAll('tspan');
      if (tspans.length) {
        let dxSubsetIndex = 0;
        const updatedDx = [...dx];
        tspans.forEach(tspan => {
          const tspanDx = dx.slice(dxSubsetIndex, dxSubsetIndex + tspan.textContent.length);
          const conversionData = convertSingleElement(tspan, tspanDx);
          if (conversionData.updateRequired) {
            updatedDx.splice(dxSubsetIndex, 1, ...conversionData.updatedDx);
            tspan.textContent = conversionData.updatedTextContent;
          }
          dxSubsetIndex += tspan.textContent.length;
        });
        textElement.setAttribute('dx', updatedDx.join(' '));
      } else {
        const conversionData = convertSingleElement(textElement, dx);
        if (conversionData.updateRequired) {
          textElement.setAttribute('dx', conversionData.updatedDx.join(' '));
          textElement.textContent = conversionData.updatedTextContent;
        }
      }
    });

    // How wide is a space?
    function getSpaceWidth (element) {
      const textContent = element.textContent;
      let spaceIndex = textContent.indexOf(String.fromCharCode(160));
      if (spaceIndex === -1) {
        spaceIndex = textContent.indexOf(' ');
      }

      if (spaceIndex === -1) {
        const elementClone = element.cloneNode(true);
        elementClone.textContent = String.fromCharCode(160);
        element.parentElement.append(elementClone);
        const spaceWidth = elementClone.getExtentOfChar(0).width;
        element.parentElement.removeChild(elementClone);
        return spaceWidth;
      } else {
        return element.getExtentOfChar(spaceIndex).width;
      }
    }

    // Get updated data for conversion
    function doConversion (spaceWidth, elementDx, startIndex) {
      let changed = false;
      let index = -1;
      let spaces = '';
      let dx = [];

      const space = String.fromCharCode(160);
      for (let i = startIndex; i >= 0; i--) {
        if (elementDx[i] <= 2 * spaceWidth) continue;
        const spaceCount = Math.floor(elementDx[i] / spaceWidth);
        spaces = Array.from({ length: spaceCount }).fill(space).join('');
        index = i;
        dx = Array.from({ length: spaceCount }).fill(0);
        dx[dx.length - 1] = elementDx[i] - spaceCount * spaceWidth;
        changed = true;
        break;
      }
      return { changed, index, spaces, dx };
    }

    // Convert a single element (text or tspan)
    function convertSingleElement (element, elementDx) {
      const spaceWidth = getSpaceWidth(element);
      let updateRequired = false;
      const updatedDx = [...elementDx];
      let updatedTextContent = element.textContent;
      let conversionData = { index: elementDx.length };
      do {
        conversionData = doConversion(spaceWidth, elementDx, conversionData.index - 1);
        if (conversionData.changed) {
          updatedDx.splice(conversionData.index, 1, ...conversionData.dx);
          updatedTextContent = updatedTextContent.slice(0, conversionData.index) + conversionData.spaces + updatedTextContent.slice(conversionData.index);
          updateRequired = true;
        }
      } while (conversionData.changed);

      return {
        updateRequired, updatedDx, updatedTextContent
      };
    }
  },

  getParagraphIndexesFromCursorSelection (textarea, range) {
    const textArray = textarea.text.dom.querySelectorAll('text');
    const lineArray = INKIVE.Helpers.getLineArray(textarea.text.dom, textArray.length);
    const startOutput = INKIVE.Helpers.getCursorLineIndex(lineArray, range.start);
    const endOutput = INKIVE.Helpers.getCursorLineIndex(lineArray, range.end);

    const startIndex = this.getParagraphIndex(textarea.text, startOutput[0]);
    const endIndex = this.getParagraphIndex(textarea.text, endOutput[0]);

    const size = endIndex - startIndex + 1;
    return [...Array(size).keys()].map(i => i + startIndex);
  },

  getParagraphCount (textarea) {
    return textarea.text.strings.filter(s => s.isEol).length;
  },

  getParagraphIndex (text, lineIndex) {
    return text.strings.slice(0, lineIndex).filter(s => s.isEol).length;
  },

  handleAlignmentIcon (handleRtl, handleLtr, textAlignButton, isJapanese) {
    handleRtl && textAlignButton.classList.add('rtl-applied');
    handleLtr && textAlignButton.classList.remove('rtl-applied');
    textAlignButton.querySelectorAll('.horizontal icon').forEach(b => {
      const link = b.parentElement;
      const li = link.closest('li');
      const iconHref = b.querySelector('use').getAttribute('xlink:href');

      switch (iconHref) {
        case '#text---align-left':
          if (!handleRtl) break;
          b.querySelector('use').setAttribute('xlink:href', '#text---align-right');
          link.setAttribute('data-original-title', INKIVE.TranslateHelper.translate('popupmenu.tooltip.align_right'));
          link.setAttribute('data-title', INKIVE.TranslateHelper.translate('popupmenu.tooltip.align_right'));
          break;
        case '#text---align-right':
          if (!handleRtl) break;
          b.querySelector('use').setAttribute('xlink:href', '#text---align-left');
          link.setAttribute('data-original-title', INKIVE.TranslateHelper.translate('popupmenu.tooltip.align_left'));
          link.setAttribute('data-title', INKIVE.TranslateHelper.translate('popupmenu.tooltip.align_left'));
          break;
        case '#text---align-justify-all':
        case '#text---align-justify-start':
        case '#text---align-justify-middle':
        case '#text---align-justify-end':
          if (handleRtl || isJapanese) li.style.display = 'none';
          if (handleLtr) li.style.display = '';
          break;
      }

      INKIVE.Helpers.initTooltips();
    });
  },

  getLineSpacing (textElem, dom) {
    let lineSpacing;
    let fontSizeText = textElem.getAttribute('font-size') || (dom && dom.getAttribute('font-size'));
    fontSizeText = fontSizeText && fontSizeText.replace(/[^0-9.]+/, '');
    const fontSize = parseFloat(fontSizeText) || 22;
    const globalDy = textElem.getAttribute('data-dy');
    const classList = textElem.getAttribute('class') || '';
    const tspans = [...textElem.querySelectorAll(':scope > tspan, :scope > text')];

    if (globalDy) {
      lineSpacing = globalDy.split(' ').filter(Boolean).map(dy => Math.round(dy * 100) / 100);
    } else {
      lineSpacing = (/line-spacing-([-+]?[0-9]*\.?[0-9]+)/.test(classList))
        ? parseFloat(classList.match(/line-spacing-([-+]?[0-9]*\.?[0-9]+)/i)[1])
        : 0;

      if (!textElem.classList.contains('text') && !lineSpacing && tspans.length >= 2) {
        if (tspans[1].getAttribute('y')) {
          lineSpacing = parseFloat(tspans[1].getAttribute('y') - tspans[0].getAttribute('y') - fontSize);
        }
      }
    }

    return lineSpacing || 0;
  },

  getLineMargin (textElem) {
    const classList = textElem.getAttribute('class') || '';
    const match = classList.match(/ line-margin-([-+]?[0-9]*\.?[0-9]+)/i);
    if (match) {
      return parseFloat(match[1]);
    }

    const dataMargin = textElem.getAttribute('data-margin');
    if (!dataMargin) return 0;

    return dataMargin.split(' ').filter(Boolean).map(m => Math.round(m * 100) / 100);
  },

  getParagraphSpacing (textElem) {
    const classList = textElem.getAttribute('class') || '';
    const match = classList.match(/paragraph-spacing-([-+]?[0-9]*\.?[0-9]+)/i);
    if (match) {
      return parseFloat(match[1]);
    }

    const dataParagraphDy = textElem.getAttribute('data-paragraph-dy');
    if (dataParagraphDy) {
      return dataParagraphDy.split(' ').filter(Boolean).map(m => Math.round(m * 100) / 100);
    }

    return 0;
  },

  getFirstLineMargin (textElem) {
    const classList = textElem.getAttribute('class') || '';
    const match = classList.match(/first-line-margin-([-+]?[0-9]*\.?[0-9]+)/i);
    if (match) {
      return parseInt(match[1]);
    }

    const dataFirstLineMargin = textElem.getAttribute('data-first-line-margin');
    if (dataFirstLineMargin) {
      return dataFirstLineMargin.split(' ').filter(Boolean).map(m => Math.round(m * 100) / 100);
    }

    return 0;
  },

  getRightMargin (textElem) {
    const classList = textElem.getAttribute('class') || '';
    const match = classList.match(/right-margin-([-+]?[0-9]*\.?[0-9]+)/i);
    if (match) {
      return parseInt(match[1]);
    }

    const dataRightMargin = textElem.getAttribute('data-right-margin');
    if (dataRightMargin) {
      return dataRightMargin.split(' ').filter(Boolean).map(m => Math.round(m * 100) / 100);
    }

    return 0;
  },

  getTextLengthBeforeLineIndex (textarea, lineIndex) {
    let length = 0;
    for (let i = 0; i < lineIndex; i++) {
      if (!textarea.strings[i]) break;
      length += textarea.strings[i].textContent.length;
    }
    return length;
  },

  getTotalLetterSpacing (letterSpacingArray, startIndex, endIndex, isTableText) {
    if (isTableText) return 0;

    return Array.isArray(letterSpacingArray)
      ? letterSpacingArray.slice(startIndex, endIndex).reduce((a, b) => a + b, 0)
      : letterSpacingArray * Math.abs(endIndex - startIndex - 1) || 0;
  },

  convertLetterSpacing (value, fontSize) {
    return (parseFloat(value) / 1000 * fontSize).toFixed(2);
  }
});

export function getFontStretch (fontStretch) {
  const lowerCaseFontStretch = fontStretch?.toLowerCase();
  switch (lowerCaseFontStretch) {
    case 'condensed':
    case 'cond':
      return 'condensed';
    case 'compressed':
    case 'comp':
      return 'ultra-condensed';
    default:
      return 'normal';
  }
};
